export type ShortcutAction =
  | 'tool.select'
  | 'tool.character'
  | 'tool.facing'
  | 'tool.path'
  | 'tool.camera'
  | 'file.load'
  | 'edit.undo'
  | 'edit.redo'
  | 'edit.deleteSelected'
  | 'edit.clearPath'
  | 'export.png'
  | 'copy.prompt'
  | 'copy.json'
  | 'view.cancel';

export type ShortcutMap = Record<ShortcutAction, string[]>;

export type ShortcutDefinition = {
  action: ShortcutAction;
  label: string;
  detail?: string;
  defaultCombos: string[];
};

export type ShortcutGroupDefinition = {
  title: string;
  definitions: ShortcutDefinition[];
};

export type ShortcutHelpItem = {
  label: string;
  keys: string[];
  detail?: string;
};

export type ShortcutHelpGroup = {
  title: string;
  items: ShortcutHelpItem[];
};

export const SHORTCUT_STORAGE_KEY = 'shotmap-studio-shortcuts-v1';

export const shortcutGroups: ShortcutGroupDefinition[] = [
  {
    title: '工具切换',
    definitions: [
      { action: 'tool.select', label: '选择/移动', defaultCombos: ['V', '1'] },
      { action: 'tool.character', label: '添加角色', defaultCombos: ['R', '2'] },
      { action: 'tool.facing', label: '设置朝向', defaultCombos: ['F', '3'] },
      { action: 'tool.path', label: '添加路径', defaultCombos: ['P', '4'] },
      { action: 'tool.camera', label: '添加机位', defaultCombos: ['C', '5'] }
    ]
  },
  {
    title: '编辑与导出',
    definitions: [
      { action: 'file.load', label: '载入底图', defaultCombos: ['L'] },
      { action: 'edit.undo', label: '撤销', defaultCombos: ['Mod+Z'] },
      { action: 'edit.redo', label: '重做', defaultCombos: ['Mod+Shift+Z', 'Mod+Y'] },
      { action: 'edit.deleteSelected', label: '删除选中对象', defaultCombos: ['Delete', 'Backspace'] },
      { action: 'edit.clearPath', label: '清除角色路径', detail: '需要先选中角色', defaultCombos: ['X'] },
      { action: 'export.png', label: '导出 PNG', defaultCombos: ['E'] },
      { action: 'copy.prompt', label: '复制 Prompt', defaultCombos: ['Shift+P'] },
      { action: 'copy.json', label: '复制 JSON', defaultCombos: ['Shift+J'] },
      { action: 'view.cancel', label: '取消当前选择', defaultCombos: ['Escape'] }
    ]
  }
];

export const fixedShortcutHelpGroup: ShortcutHelpGroup = {
  title: '精细调整',
  items: [
    { label: '移动选中对象', keys: ['方向键'] },
    { label: '快速移动', keys: ['Shift + 方向键'] },
    { label: '微调移动', keys: ['Alt + 方向键'] }
  ]
};

export const shortcutDefinitions = shortcutGroups.flatMap((group) => group.definitions);

const modifierKeys = new Set(['Alt', 'Control', 'Meta', 'Shift']);

function actionExists(action: string): action is ShortcutAction {
  return shortcutDefinitions.some((definition) => definition.action === action);
}

export function getShortcutDefinition(action: ShortcutAction) {
  return shortcutDefinitions.find((definition) => definition.action === action) ?? null;
}

export function createDefaultShortcutMap(): ShortcutMap {
  return shortcutDefinitions.reduce((result, definition) => {
    result[definition.action] = [...definition.defaultCombos];
    return result;
  }, {} as ShortcutMap);
}

export function normalizeShortcutMap(value: unknown): ShortcutMap {
  const defaults = createDefaultShortcutMap();
  if (!value || typeof value !== 'object') return defaults;
  const record = value as Partial<Record<ShortcutAction, unknown>>;
  shortcutDefinitions.forEach((definition) => {
    const saved = record[definition.action];
    if (!Array.isArray(saved)) return;
    const combos = saved.filter((combo): combo is string => typeof combo === 'string' && combo.trim().length > 0);
    if (combos.length) defaults[definition.action] = Array.from(new Set(combos));
  });
  return defaults;
}

export function readShortcutMap(): ShortcutMap {
  try {
    const saved = window.localStorage.getItem(SHORTCUT_STORAGE_KEY);
    if (saved) return normalizeShortcutMap(JSON.parse(saved));
  } catch {
    // Local storage may be unavailable in private contexts.
  }
  return createDefaultShortcutMap();
}

export function saveShortcutMap(shortcuts: ShortcutMap) {
  window.localStorage.setItem(SHORTCUT_STORAGE_KEY, JSON.stringify(shortcuts));
}

function keyName(key: string) {
  if (key === ' ') return 'Space';
  if (key === 'Esc') return 'Escape';
  if (key.length === 1) return key.toUpperCase();
  return key;
}

export function eventToShortcutCombo(event: KeyboardEvent): string | null {
  if (modifierKeys.has(event.key)) return null;
  const parts: string[] = [];
  if (event.metaKey || event.ctrlKey) {
    parts.push('Mod');
  }
  if (event.shiftKey) parts.push('Shift');
  if (event.altKey) parts.push('Alt');
  parts.push(keyName(event.key));
  return parts.join('+');
}

export function shortcutActionForEvent(event: KeyboardEvent, shortcuts: ShortcutMap): ShortcutAction | null {
  const combo = eventToShortcutCombo(event);
  if (!combo) return null;
  return shortcutDefinitions.find((definition) => shortcuts[definition.action]?.includes(combo))?.action ?? null;
}

export function findShortcutConflict(shortcuts: ShortcutMap, action: ShortcutAction, combo: string) {
  return shortcutDefinitions.find((definition) => definition.action !== action && shortcuts[definition.action]?.includes(combo)) ?? null;
}

export function formatShortcutCombo(combo: string) {
  return combo
    .split('+')
    .map((part) => {
      if (part === 'Mod') return 'Cmd/Ctrl';
      if (part === 'Escape') return 'Esc';
      if (part === 'ArrowUp') return '上箭头';
      if (part === 'ArrowDown') return '下箭头';
      if (part === 'ArrowLeft') return '左箭头';
      if (part === 'ArrowRight') return '右箭头';
      return part;
    })
    .join(' + ');
}

export function buildShortcutHelpGroups(shortcuts: ShortcutMap): ShortcutHelpGroup[] {
  return [
    ...shortcutGroups.map((group) => ({
      title: group.title,
      items: group.definitions.map((definition) => ({
        label: definition.label,
        detail: definition.detail,
        keys: shortcuts[definition.action].map(formatShortcutCombo)
      }))
    })),
    fixedShortcutHelpGroup
  ];
}

export function isEditableShortcutTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;
  const editable = target.closest('input, textarea, select, [contenteditable="true"]');
  return Boolean(editable);
}

export function isShortcutAction(value: string): value is ShortcutAction {
  return actionExists(value);
}
